// noinspection JSUnusedGlobalSymbols
import type { CSSProperties } from 'react';

import { createTheme } from '@mui/material';
import type { TypographyOptions, TypographyStyleOptions } from '@mui/material/styles/createTypography';

import Colors from 'theme/colors';
import Components from 'theme/components';

declare module '@mui/material/styles' {
  interface Palette {
    colors: typeof Colors;
  }

  interface PaletteOptions {
    colors: typeof Colors;
  }

  interface TypographyVariants {
    buttonSmall: CSSProperties;
    buttonMedium: CSSProperties;
    inputLabel: CSSProperties;
    helperText: CSSProperties;
    inputText: CSSProperties;
  }

  interface Typography {
    buttonSmall: TypographyVariants['buttonSmall'];
    buttonMedium: TypographyVariants['buttonMedium'];
    inputLabel: TypographyVariants['inputLabel'];
    helperText: TypographyVariants['helperText'];
    inputText: TypographyVariants['inputText'];
  }
}

declare module '@mui/material/Badge' {
  interface BadgePropsColorOverrides {
    grey: true;
  }

  interface ButtonPropsVariantOverrides {
    upload: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonClasses {
    upload: true;
  }

  interface ButtonPropsVariantOverrides {
    upload: true;
  }
}

declare module '@mui/material/Fab' {
  interface FabPropsColorOverrides {
    white: true;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsSizeOverrides {
    tiny: true;
  }
}

declare module '@mui/material/ListItemIcon' {
  interface ListItemIconProps {
    size?: 'tiny' | 'small' | 'medium' | 'large';
  }
}

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    default: true;
  }
}

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsSizeOverrides {
    tiny: true;
  }

  interface SvgIconPropsColorOverrides {
    default: true;
  }
}

interface CustomTypographyOptions extends TypographyOptions {
  buttonSmall?: TypographyStyleOptions;
  buttonMedium?: TypographyStyleOptions;
  inputLabel?: TypographyStyleOptions;
  helperText?: TypographyStyleOptions;
  inputText?: TypographyStyleOptions;
}

const theme = createTheme({
  palette: {
    common: {
      black: Colors.steel[900],
      white: Colors.white[100]
    },

    primary: {
      main: Colors.cobalt[500],
      dark: Colors.cobalt[700],
      light: Colors.cobalt[400]
    },
    secondary: {
      main: Colors.coral[500],
      dark: Colors.coral[700],
      light: Colors.coral[300]
    },

    divider: Colors.steel['A013'],

    text: {
      primary: Colors.steel[900],
      secondary: Colors.steel['A063'],
      disabled: Colors.steel['A040']
    },
    action: {
      active: Colors.steel['A059'],
      hover: Colors.steel['A004'],
      selected: Colors.cobalt['A008'],
      focus: Colors.steel['A013'],
      disabled: Colors.steel['A040'],
      disabledBackground: Colors.steel['A013']
    },
    colors: Colors
  },
  typography: {
    fontFamily: 'Roboto',
    htmlFontSize: 14,

    // h1-h3, h5 and h6 carry no overrides: headings use Roboto at MUI's default weights.
    // h1, h2 and h3 have no call sites at all, so they need no entry here.
    h4: {
      // Not typography. `variant='h4'` is used ~37 times and several call sites rely on the heading
      // stretching inside a flex row to push a trailing action to the edge; removing this would
      // silently reflow those layouts. Should move to the call sites — tracked as follow-up.
      flexGrow: '1'
    },
    button: {
      textTransform: 'none'
    }
  } as CustomTypographyOptions,
  components: Components
});

export default theme;
